import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AgentProvider } from './store/agentStore.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import AppShell from './components/layout/AppShell.jsx';
import ErrorBoundary from './components/shared/ErrorBoundary.jsx';
import RequireAuth from './components/shared/RequireAuth.jsx';
import Overview from './pages/Overview.jsx';
import SubscribersList from './pages/SubscribersList.jsx';
import DidsList from './pages/DidsList.jsx';
import RoutesList from './pages/RoutesList.jsx';
import ExtensionsList from './pages/ExtensionsList.jsx';
import ExtensionDetail from './pages/ExtensionDetail.jsx';
import LocationsList from './pages/LocationsList.jsx';
import PbxHostsList from './pages/PbxHostsList.jsx';
import PbxHostDetail from './pages/PbxHostDetail.jsx';
import DevicesList from './pages/DevicesList.jsx';
import DeviceDetail from './pages/DeviceDetail.jsx';
import SubscriberDetail from './pages/SubscriberDetail.jsx';
import LineDetail from './pages/LineDetail.jsx';
import LocationDetail from './pages/LocationDetail.jsx';
import DidDetail from './pages/DidDetail.jsx';
import AuditLog from './pages/AuditLog.jsx';
import ServiceAlerts from './pages/ServiceAlerts.jsx';
import Profile from './pages/Profile.jsx';
import Handoff from './pages/Handoff.jsx';
import OrgGate from './pages/OrgGate.jsx';

// Lazy-load heavier framework pages so the initial bundle stays small.
const Settings = lazy(() => import('./pages/Settings.jsx'));
const UserManagement = lazy(() => import('./pages/UserManagement.jsx'));

const ADMIN_CONSOLE_URL = 'https://admin.verso-cloud.io';

function PageFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}

// Gates the app: shows a full-screen error/instructional card for every
// non-authenticated state (admin-only, wrong-org, not-provisioned, unable-to-connect).
function AuthGate({ children }) {
  const { loading, isAuthenticated, error, errorType, refetch } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="mt-4 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (errorType === 'admin_console_required') {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Verso Operator Dashboard</h1>
            <p className="mt-2 text-sm text-gray-600">
              This is the org dashboard. Colorado Communications administrators must sign in via the{' '}
              <a href={ADMIN_CONSOLE_URL} className="font-medium text-blue-600 hover:text-blue-800">
                admin console
              </a>.
            </p>
          </div>
        </div>
      );
    }

    if (errorType === 'org_mismatch') {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Wrong Organization</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your account does not have access to this organization. Check the URL or contact your administrator.
            </p>
          </div>
        </div>
      );
    }

    if (errorType === 'not_provisioned') {
      return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
            <h1 className="text-xl font-bold text-gray-900">Access Not Provisioned</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your account has not been provisioned for Verso. Please contact your organization administrator.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-gray-900">Unable to Connect</h1>
          <p className="mt-2 text-sm text-gray-600">
            {error || 'Could not reach the Verso API. Please try again later.'}
          </p>
          <button
            onClick={refetch}
            className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return children;
}

function AuthenticatedApp() {
  return (
    <AuthGate>
      <AgentProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/"            element={<ErrorBoundary><Overview /></ErrorBoundary>} />
            {/* Verso business pages — placeholders until the corresponding Worker routes land. */}
            <Route path="/subscribers" element={<ErrorBoundary><SubscribersList /></ErrorBoundary>} />
            <Route path="/subscribers/:id" element={<ErrorBoundary><SubscriberDetail /></ErrorBoundary>} />
            {/* A line is the thing we sell, so it gets a page like every other object. */}
            <Route path="/lines/:id"   element={<ErrorBoundary><LineDetail /></ErrorBoundary>} />
            <Route path="/dids"        element={<ErrorBoundary><DidsList /></ErrorBoundary>} />
            <Route path="/dids/:e164"  element={<ErrorBoundary><DidDetail /></ErrorBoundary>} />
            <Route path="/routes"      element={<ErrorBoundary><RoutesList /></ErrorBoundary>} />
            <Route path="/extensions"      element={<ErrorBoundary><ExtensionsList /></ErrorBoundary>} />
            <Route path="/extensions/:id"  element={<ErrorBoundary><ExtensionDetail /></ErrorBoundary>} />
            <Route path="/devices"     element={<ErrorBoundary><DevicesList /></ErrorBoundary>} />
            <Route path="/devices/:id" element={<ErrorBoundary><DeviceDetail /></ErrorBoundary>} />
            <Route path="/locations"   element={<ErrorBoundary><LocationsList /></ErrorBoundary>} />
            <Route path="/locations/:id" element={<ErrorBoundary><LocationDetail /></ErrorBoundary>} />
            <Route path="/pbx-hosts"   element={<ErrorBoundary><PbxHostsList /></ErrorBoundary>} />
            <Route path="/pbx-hosts/:agentId" element={<ErrorBoundary><PbxHostDetail /></ErrorBoundary>} />
            {/* org_admin only. Removing it from the sidebar hid it; RequireAuth is
                what actually withholds it, since a bookmarked /audit would
                otherwise still render for an org_ro user. */}
            <Route path="/audit"       element={<ErrorBoundary><RequireAuth role="org_admin"><AuditLog /></RequireAuth></ErrorBoundary>} />
            <Route path="/alerts"      element={<ErrorBoundary><ServiceAlerts /></ErrorBoundary>} />

            {/* Framework pages. */}
            <Route path="/profile"  element={<ErrorBoundary><Profile /></ErrorBoundary>} />
            <Route path="/settings" element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><Settings /></Suspense></RequireAuth></ErrorBoundary>} />
            <Route path="/users"    element={<ErrorBoundary><RequireAuth role="org_admin"><Suspense fallback={<PageFallback />}><UserManagement /></Suspense></RequireAuth></ErrorBoundary>} />
          </Route>
        </Routes>
      </AgentProvider>
    </AuthGate>
  );
}

// The subscriber portal lives at /portal/*. Its identity model is entirely
// separate from the staff dashboard (magic-link email → long-lived JWT in
// localStorage), so it owns its own auth provider + routing tree rather
// than sharing the staff AuthProvider.
const PortalApp = lazy(() => import('./portal/PortalApp.jsx'));

export default function App() {
  // The apex (verso-cloud.io) has no org in front of it, so there is no session
  // to mint and nothing to load — ask which organisation and send them to its
  // subdomain. Checked before any provider mounts: AuthProvider would otherwise
  // POST /auth/session with no slug and fail in a way that reads like an outage.
  // Dev (localhost) returns null from getOrgSlug too, so the apex is identified
  // by hostname rather than by the absence of a slug.
  if (typeof window !== 'undefined'
      && window.location.hostname === (import.meta.env.VITE_PRODUCT_DOMAIN || 'verso-cloud.io')) {
    return <OrgGate />;
  }
  return (
    <Routes>
      {/* Subscriber portal — completely separate auth surface. Mounted
          FIRST so its own AuthProvider handles /portal/* without staff
          auth interfering. */}
      <Route
        path="/portal/*"
        element={
          <Suspense fallback={<PageFallback />}>
            <PortalApp />
          </Suspense>
        }
      />

      {/* Everything else is the staff dashboard. */}
      <Route
        path="*"
        element={
          <AuthProvider>
            <Routes>
              {/* /handoff sits outside AuthGate — the Handoff page uses
                  the one-time KV token from the admin console. */}
              <Route path="/handoff" element={<Handoff />} />
              <Route path="*"        element={<AuthenticatedApp />} />
            </Routes>
          </AuthProvider>
        }
      />
    </Routes>
  );
}
